<template>
  <div class="relative" v-click-outside="close">
    <input
        type="text"
        class="form-control"
        :value="displayValue"
        :data-cy="dataCy"
        placeholder="Pick a time"
        autocomplete="off"
        @focus="open"
        @click="open"
        @input="typed = $event.target.value"
        @keydown.enter.prevent="commitTyped"
        @keydown.esc="close"
        @blur="commitTyped"
    >
    <div v-if="isOpen" ref="list"
         class="absolute w-full bg-white border border-solid border-gray-200 rounded-xxs"
         style="z-index: 100; max-height: 200px; overflow-y: auto; top: 100%; left: 0; margin-top: 2px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
      <div v-for="option in options" :key="option.value"
           :ref="option.value === scrollAnchor ? 'anchor' : null"
           class="cursor-pointer p-x-sm p-y-xs text-14 flex justify-space-between"
           :class="option.value === value ? 'bg-green-50 text-green-700 font-semi' : 'text-gray-900'"
           @mousedown.prevent="pick(option)">
        <span>{{ option.label }}</span>
        <span v-if="option.duration" class="text-gray-400">{{ option.duration }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// Google Calendar-style time combobox: a text input that opens a scrollable
// dropdown of 15-minute increments but still accepts free-typed times
// ("5:13 pm", "5pm", "17:00"). Native <input type="time"> can't do this — its
// `step` attribute only affects validation, not the browser picker UI.
//
// v-model value is a 24h "HH:mm" string. When `baseTime` is set (the end-time
// field on a same-day event), options start just after it and carry duration
// hints ("30 mins", "1.5 hrs"), matching the old jQuery timepicker behavior.
const STEP_MINUTES = 15;

export default {
  name: 'time-select',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    // Start time ("HH:mm") this field is relative to; enables duration labels
    // and starts the list right after it. Pass null for a plain full-day list.
    baseTime: {
      type: String,
      required: false,
      default: null,
    },
    dataCy: {
      type: String,
      required: false,
      default: null,
    },
    // Where to scroll the open list when the field has no value yet. Without
    // this an empty field opens scrolled to midnight, so reaching a typical
    // afternoon start means a long scroll. Anchoring mid-afternoon puts the
    // common after-school/evening practice band in view immediately.
    defaultTime: {
      type: String,
      required: false,
      default: '15:00',
    },
  },
  data() {
    return {
      isOpen: false,
      typed: null,
    };
  },
  directives: {
    // Minimal click-outside so an open list closes when clicking elsewhere
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value();
          }
        };
        document.addEventListener('mousedown', el.clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el.clickOutsideHandler);
      },
    },
  },
  computed: {
    // The option the open list scrolls to: the current value when set,
    // otherwise the default anchor. May not exist in `options` (e.g. an end
    // field whose list starts after a later baseTime) — then open() just
    // leaves the list at the top, which is the sensible spot anyway.
    scrollAnchor() {
      return this.value || this.defaultTime;
    },
    displayValue() {
      if (this.typed !== null) {
        return this.typed;
      }
      return this.format(this.value);
    },
    options() {
      const opts = [];
      const baseMinutes = this.baseTime ? this.toMinutes(this.baseTime) : null;
      const startAt = baseMinutes !== null ? baseMinutes + STEP_MINUTES : 0;
      const endAt = baseMinutes !== null ? baseMinutes + (24 * 60) : 24 * 60;
      for (let mins = startAt; mins < endAt; mins += STEP_MINUTES) {
        // Same-day list only: stop at midnight (overnight events use the end date field)
        if (mins >= 24 * 60) {
          break;
        }
        const value = this.toHHmm(mins);
        opts.push({
          value,
          label: this.format(value),
          duration: baseMinutes !== null ? this.durationLabel(mins - baseMinutes) : null,
        });
      }
      return opts;
    },
  },
  methods: {
    open() {
      this.isOpen = true;
      this.$nextTick(() => {
        const anchor = this.$refs.anchor && this.$refs.anchor[0];
        if (anchor && this.$refs.list) {
          this.$refs.list.scrollTop = Math.max(anchor.offsetTop - 60, 0);
        }
      });
    },
    close() {
      this.isOpen = false;
      this.typed = null;
    },
    pick(option) {
      this.$emit('input', option.value);
      this.close();
    },
    commitTyped() {
      if (this.typed !== null) {
        const parsed = moment(this.typed.trim(), ['h:mm a', 'h:mma', 'h a', 'ha', 'HH:mm', 'H:mm'], false);
        if (parsed.isValid()) {
          this.$emit('input', parsed.format('HH:mm'));
        }
        // Invalid input just reverts to the last good value
      }
      this.close();
    },
    format(hhmm) {
      const parsed = moment(hhmm, 'HH:mm');
      return parsed.isValid() ? parsed.format('h:mm a') : '';
    },
    toMinutes(hhmm) {
      const [h, m] = hhmm.split(':').map(Number);
      return (h * 60) + m;
    },
    toHHmm(mins) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    },
    // Hours + minutes ("1 hr 15 mins"), matching the "Event length" line in
    // date_time_range. Decimal hours ("1.3 hrs") read as odd fractions here.
    durationLabel(minutes) {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const parts = [];
      if (hrs > 0) {
        parts.push(`${hrs} hr${hrs === 1 ? '' : 's'}`);
      }
      if (mins > 0 || parts.length === 0) {
        parts.push(`${mins} min${mins === 1 ? '' : 's'}`);
      }
      return parts.join(' ');
    },
  },
};
</script>
